import React from "react";
import { useDispatch } from "react-redux";

import InviteService from "notifications/api/InviteService";
import { showNotifyError, showNotifySuccess } from "services/toaster";

import ConfirmationDialog from "components/Dialogs/ConfirmationDialog";
import NotificationDialog from "components/Dialogs/NotificationDialog";
import RejectionDialog from "components/Dialogs/RejectionDialog";
import { Status } from "components";

import { useAppDispatch, useAppSelector } from "store";
import { getAllInboxNotificationsList, markAsViewed, openedNotificationSet, selectSlideOut } from "slices/notificationsSlice";
import FormDescription from "./FormDescription";
import { RejectionReasonWrapper, RejectionReasonTitle, RejectionReasonText } from "./styles";

const RegFormSubmitted = ({ notify }) => {
  const { isCompany } = notify;

  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const slideOut = useAppSelector(selectSlideOut);

  const handleClose = (event) => {
    event.stopPropagation();
    dispatch(openedNotificationSet(null));
  };

  // Go to Second Dialog
  const handleReject = (event) => {
    event.stopPropagation();
    dispatch(openedNotificationSet({ ...notify, rejectionInProgress: true }));
  };

  // Return to Previous Dialog
  const handleCancelRejection = (event) => {
    event.stopPropagation();
    dispatch(openedNotificationSet({ ...notify, rejectionInProgress: false }));
  };

  const handleSendRejectionReason = async (values, formikBag) => {
    try {
      const { inviteId, userFullName, companyName, email } = notify.data;

      await isCompany
        ? InviteService.rejectCompany(inviteId, values.rejectionReason)
        : InviteService.reject(inviteId, values.rejectionReason);
      await appDispatch(markAsViewed(notify.id));
      await dispatch(openedNotificationSet(null));
      await appDispatch(getAllInboxNotificationsList());
      showNotifySuccess(`${isCompany ? companyName : userFullName} registration was successfully rejected. The notification email was sent to ${email}.`);
    } catch (error) {
      const { fieldErrors } = error;

      if (fieldErrors) {
        formikBag.setErrors(fieldErrors);
      } else {
        showNotifyError(error);
      }
    }
  };

  const handleGiveAccess = async (event) => {
    event.stopPropagation();
    try {
      const { inviteId, userFullName, companyName, email } = notify.data;

      await isCompany
        ? InviteService.approveCompany(inviteId)
        : InviteService.approve(inviteId);
      await appDispatch(markAsViewed(notify.id));
      await appDispatch(openedNotificationSet(null));
      await appDispatch(getAllInboxNotificationsList());
      showNotifySuccess(`${isCompany ? companyName : userFullName} registration was successfully accepted. The confirmation email was sent to ${email}.`);
    } catch (error) {
      if (error.status === 400.5) {
        showNotifyError("The contact was deleted, you cannot approve or reject it.");
      } else {
        showNotifyError(error);
      }
    }
  };

  if (notify.rejectionInProgress) {
    return (
      <RejectionDialog
        onCancel={handleCancelRejection}
        onClose={handleCancelRejection}
        onSubmit={handleSendRejectionReason}
      />
    );
  }

  if (notify.data.status === "Pending") {
    return (
      <ConfirmationDialog
        open
        borderedTitle
        projectName={notify.data.projectName}
        title="Registration Form Submitted"
        cancelButtonTitle="Reject"
        confirmButtonTitle="Give Access"
        onClose={handleClose}
        onCancel={handleReject}
        onConfirm={handleGiveAccess}
        slideOut={slideOut}
        animations
        withScroll
      >
        <FormDescription notify={notify} />
      </ConfirmationDialog>
    );
  }

  if (notify.data.status === "Approved") {
    return (
      <NotificationDialog
        open
        title="Registration Form Submitted"
        footer={<Status status="accepted" />}
        onClose={handleClose}
        slideOut={slideOut}
        animations
      >
        <FormDescription notify={notify} />
      </NotificationDialog>
    );
  }

  return (
    <NotificationDialog
      open
      title="Registration Form Submitted"
      footer={<Status status="rejected" />}
      onClose={handleClose}
      slideOut={slideOut}
      animations
    >
      <>
        <FormDescription notify={notify} />
        <br />
        <RejectionReasonWrapper>
          <RejectionReasonTitle>Rejection Reason</RejectionReasonTitle>
          <RejectionReasonText>
            {notify.data.rejectionReason || "Rejection reason undefined. Something went wrong."}
          </RejectionReasonText>
        </RejectionReasonWrapper>
      </>
    </NotificationDialog>
  );
};

export default RegFormSubmitted;
